import {
  useContext, useEffect, useMemo, useState,
} from 'react';
import BlockItem from '../../../components/BlockItem/BlockItem';
import BlockModal from './BlockModal';
import BlockDeleteModal from './BlockDeleteModal';
import { TemplatesContext } from '../../../contexts/TemplatesContext';
import Template from '../../../models/Template/Template';
import { URLContext } from '../../../contexts/URLContext';
import { CategoriesContext } from '../../../contexts/CategoriesContext';
import Paginator from '../../../components/Paginator';
import { PaginationMetaLink, PaginatorProps } from '../../../models/Paginator';

const perPage = 16;

const Index = () => {
  const [newBlockModalOpen, setNewBlockModalOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState('');
  const [deletingTemplate, setDeletingTemplate] = useState<Template>(null); // blockId
  const { categoryId } = useContext(URLContext);
  const { requestCategoryList } = useContext(CategoriesContext);
  const {
    templateList, submitTemplate, destroyTemplate, getAllTemplates, getTemplatesFromCategory,
  } = useContext(TemplatesContext);
  const [inputText, setInputText] = useState('');

  const [paginationCurrentPage, setPaginationCurrentPage] = useState(1);
  const [paginationLinks, setPaginationLinks] = useState<PaginationMetaLink[]>();
  const [paginationTemplates, setPaginationTemplates] = useState({ total: 0, from: 0, to: 0 });

  const requestAllTemplates = async (page?: number, perPage?: number) => {
    let result: PaginatorProps = null;
    if (categoryId === null || categoryId.trim().length === 0) {
      result = await getAllTemplates(page, perPage, inputText);
    } else {
      result = await getTemplatesFromCategory(categoryId, page, perPage, inputText);
    }

    setPaginationLinks(result.links);
    setPaginationCurrentPage(result.currentPage);
    setPaginationTemplates({ total: result.total, from: result.from, to: result.to });
  };

  // fetch categories
  useEffect(() => {
    requestCategoryList();
  }, [requestCategoryList]);

  // カテゴリーが変わった場合はcurrentPageをリセットします。
  useEffect(() => {
    setInputText('');
    setPaginationCurrentPage(1);
    requestAllTemplates(1, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  // 最初のfetch
  useEffect(() => {
    // すべてまtemplatesを選んだ場合
    requestAllTemplates(1, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 新規追加また編集リクエストを投げます
  const handleSubmitTemplate = (template: Template) => {
    setNewBlockModalOpen(false);
    // リクエストが終わりましたら、もう一度fetchします。
    submitTemplate(template, () => {
      requestAllTemplates(paginationCurrentPage);
    });
    setEditingTemplateId('');
  };

  const handleDeleteSubmit = () => {
    if (deletingTemplate === null) {
      return;
    }
    destroyTemplate(deletingTemplate.id, () => {
      setDeletingTemplate(null);
      requestAllTemplates(paginationCurrentPage);
    });
  };

  const handleSubmitSearchText = () => {
    requestAllTemplates(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmitSearchText();
    }
  };

  const editingTemplate = useMemo(() => templateList.find((t) => t.id === editingTemplateId), [editingTemplateId, templateList]);

  return (
    (
      <main className="content">
        <div className="container-fluid p-0">
          <div className="mb-3">
            <h3 className="h3 d-inline align-middle">
              システムブロック
              管理
            </h3>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h4 className="h4 float-start mt-2">
                    システムブロック
                    一覧
                  </h4>
                  <div className="float-end mr-1">
                    <button type="button" className="btn btn-primary" onClick={() => setNewBlockModalOpen(true)}>新規登録</button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row mb-1 block-list-header">
                    <div className="col">
                      <div className="d-flex justify-content-start mb-2">
                        <label className="col-form-label text-nowrap">検索：</label>
                        <input
                          value={inputText}
                          onChange={(e) => setInputText(e.target.value)}
                          type="text"
                          className="form-control form-search-filter"
                          id="search-filter"
                          name="search-filter"
                          onKeyDown={handleKeyDown}
                        />
                        <button type="button" onClick={handleSubmitSearchText} className="btn btn-primary">
                          <i className="fa-solid fa-magnifying-glass fa-fw" />
                        </button>
                      </div>
                    </div>
                    <div className="col">
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <div>
                          <Paginator
                            links={paginationLinks}
                            changePage={(page) => requestAllTemplates(page)}
                            currentPage={paginationCurrentPage}
                          />
                        </div>

                      </div>
                    </div>
                  </div>

                  <div
                    className="block-list blockview"
                    style={{
                      maxHeight: '60vh',
                      overflowY: 'scroll',
                      paddingLeft: '0.25rem',
                      paddingRight: '0.25rem',
                      paddingBottom: '0.75rem',
                    }}
                  >
                    {templateList.map((template) => <BlockItem imgUrl={template.img_url} key={template.id} type={template.type} title={template.title} desc={template.description} handleBlockEdit={() => { setEditingTemplateId(template.id); setNewBlockModalOpen(true); }} handleBlockDelete={() => setDeletingTemplate(template)} />)}

                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '0.75rem' }}>
                      { `全 ${paginationTemplates.total} 件中 ${paginationTemplates.from} 〜 ${paginationTemplates.to} 件を表示` }
                    </div>
                    <div>
                      <Paginator
                        links={paginationLinks}
                        changePage={(page) => requestAllTemplates(page)}
                        currentPage={paginationCurrentPage}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
        <BlockModal
          open={newBlockModalOpen}
          template={editingTemplate}
          defaultCategoryId={categoryId}
          handleClose={() => {
            setNewBlockModalOpen(false);
            setEditingTemplateId('');
          }}
          handleSubmit={handleSubmitTemplate}
        />
        <BlockDeleteModal blockTitle={deletingTemplate != null && deletingTemplate.title} open={deletingTemplate != null} handleClose={() => setDeletingTemplate(null)} handleSave={handleDeleteSubmit} />
      </main>
    )
  );
};

export default Index;
